// Social Media Embed Utilities
// Supports: TikTok, Instagram, Facebook, YouTube, Twitter/X

// Regex patterns for social media URLs
// IMPORTANT: These must match the backend patterns in /app/backend/utils/oembed.py
const SOCIAL_PATTERNS = {
  // TikTok: handles @user/video/, vm.tiktok.com/, and /t/ short URLs
  tiktok: /(?:https?:\/\/)?(?:www\.)?(?:tiktok\.com\/@[\w.-]+\/video\/(\d+)|tiktok\.com\/t\/(\w+)|vm\.tiktok\.com\/(\w+))/i,
  instagram: /(?:https?:\/\/)?(?:www\.)?instagram\.com\/(?:p|reel|reels)\/([A-Za-z0-9_-]+)/i,
  facebook: /(?:https?:\/\/)?(?:www\.)?(?:facebook\.com|fb\.watch)\/(?:watch\/?\?v=(\d+)|[\w.]+\/(?:videos|posts)\/(\d+)|reel\/(\d+))/i,
  // YouTube: handles watch?v=, shorts/, youtu.be/, and strips ?si= tracking params
  youtube: /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})(?:[?&].*)?/i,
  twitter: /(?:https?:\/\/)?(?:www\.)?(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/i,
};

// Detect social media URLs in text
export const detectSocialMediaUrls = (text) => {
  const urls = [];
  
  // Find all URLs in the text
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const matches = text.match(urlRegex) || [];
  
  for (const url of matches) {
    for (const [platform, pattern] of Object.entries(SOCIAL_PATTERNS)) {
      const match = url.match(pattern);
      if (match) {
        urls.push({
          platform,
          url: url,
          id: match[1] || match[2] || match[3],
        });
        break;
      }
    }
  }
  
  return urls;
};

// Get embed URL for a platform
export const getEmbedUrl = (platform, url, id) => {
  switch (platform) {
    case 'tiktok':
      return `https://www.tiktok.com/embed/v2/${id}`;
    case 'instagram':
      return `${url}/embed`;
    case 'youtube':
      return `https://www.youtube.com/embed/${id}`;
    case 'twitter':
      return null; // Twitter uses blockquote embed, handled separately
    case 'facebook':
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`;
    default:
      return null;
  }
};

// Embed component dimensions
export const EMBED_DIMENSIONS = {
  tiktok: { width: '100%', height: 600, maxWidth: 325 },
  instagram: { width: '100%', height: 500, maxWidth: 400 },
  youtube: { width: '100%', height: 315, maxWidth: 560 },
  facebook: { width: '100%', height: 400, maxWidth: 500 },
  twitter: { width: '100%', height: 'auto', maxWidth: 550 },
};

// Platform display names and colors
export const PLATFORM_INFO = {
  tiktok: { name: 'TikTok', color: '#000000', icon: 'fab fa-tiktok' },
  instagram: { name: 'Instagram', color: '#E4405F', icon: 'fab fa-instagram' },
  youtube: { name: 'YouTube', color: '#FF0000', icon: 'fab fa-youtube' },
  facebook: { name: 'Facebook', color: '#1877F2', icon: 'fab fa-facebook' },
  twitter: { name: 'X', color: '#000000', icon: 'fab fa-x-twitter' },
};

// Remove social media URLs from text (to avoid duplication when showing embed)
export const removeSocialMediaUrls = (text) => {
  let cleanText = text;
  
  // First, remove full URLs including query params
  // This regex matches URLs starting with http/https until whitespace or end of string
  const fullUrlRegex = /https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be|tiktok\.com|vm\.tiktok\.com|instagram\.com|facebook\.com|fb\.watch|twitter\.com|x\.com)[^\s]*/gi;
  cleanText = cleanText.replace(fullUrlRegex, '').trim();
  
  // Also apply platform-specific patterns for any edge cases
  for (const pattern of Object.values(SOCIAL_PATTERNS)) {
    cleanText = cleanText.replace(new RegExp(pattern.source + '(?:[?&][^\\s]*)?', 'gi'), '').trim();
  }
  
  // Clean up any leftover URL fragments or query params
  cleanText = cleanText.replace(/\?si=[A-Za-z0-9_-]+/g, '').trim();
  cleanText = cleanText.replace(/https?:\/\/\s*/g, '').trim();
  return cleanText;
};
